import { Menu, subMenu, getFirstAccessibleSubMenuDefinition } from "../Menu";

/**
 * Pathname only: strip hash/query tail, trim, drop trailing slash (except "/").
 */
export function normalizeLoginPath(path) {
    if (path == null || typeof path !== "string") {
        return "";
    }
    const noHash = path.split("#")[0].split("?")[0];
    const t = noHash.trim();
    if (!t) {
        return "";
    }
    if (t !== "/" && t.endsWith("/")) {
        return t.slice(0, -1);
    }
    return t;
}

function isRealPath(to) {
    return to && typeof to === "string" && to.startsWith("/");
}

function addPath(set, p) {
    const n = normalizeLoginPath(p);
    if (n) {
        set.add(n);
    }
}

/**
 * All pathnames the user may navigate to (no hash-only routes).
 */
export function collectAllowedPathnames(user) {
    const set = new Set();
    if (!user?.policy?.rights) {
        return [];
    }

    for (const [appKey, appRights] of Object.entries(user.policy.rights)) {
        if (!appRights.access) {
            continue;
        }
        const menuList = Menu[appKey];
        if (!menuList) {
            continue;
        }
        const tab_rights = user.policy.rights[appKey];

        for (const tab of menuList) {
            if (!tab?.id || !tab_rights[tab.id]?.access) {
                continue;
            }

            if (tab.isSubMenu && tab_rights[tab.id].sub_menu) {
                const subs = subMenu.filter((s) => s.page === tab.id);
                for (const sub of subs) {
                    if (!isRealPath(sub.to)) {
                        continue;
                    }
                    if (!tab_rights[tab.id].sub_menu[sub.id]?.access) {
                        continue;
                    }
                    addPath(set, sub.to);
                }
            } else {
                if (isRealPath(tab.to)) {
                    addPath(set, tab.to);
                }
                if (Array.isArray(tab.link)) {
                    for (const link of tab.link) {
                        if (isRealPath(link)) {
                            addPath(set, link);
                        }
                    }
                }
            }
        }
    }

    return Array.from(set);
}

/** Same landing as legacy Login (first app / first menu / first sub). */
export function resolveFirstAccessibleLanding(user) {
    const filteredApp = Object.entries(user.policy.rights)
        .filter(([, app]) => app.access)
        .map(([key, app]) => ({ key, ...app }))
        .sort((a, b) => (a.order < b.order ? -1 : 1));

    if (filteredApp.length === 0) {
        return "/";
    }

    const appName = filteredApp[0].key;
    const tab_rights = user.policy.rights[appName];
    const filteredMenu = Menu[appName].filter((tab) => tab_rights[tab.id]?.["access"]);

    if (filteredMenu.length === 0) {
        return "/";
    }

    const firstMenu = filteredMenu[0];
    if (firstMenu.isSubMenu && tab_rights[firstMenu.id]?.sub_menu) {
        const firstSub = getFirstAccessibleSubMenuDefinition(
            firstMenu.id,
            tab_rights[firstMenu.id].sub_menu,
        );
        if (firstSub && isRealPath(firstSub.to)) {
            return firstSub.to;
        }
    }

    return isRealPath(firstMenu.to) ? firstMenu.to : "/";
}

/**
 * Post-login URL: default_page if allowed or closest allowed prefix/super-path, else first accessible.
 */
export function resolveLoginLanding(user) {
    const wanted = normalizeLoginPath(user?.default_page || "");
    if (!wanted) {
        return resolveFirstAccessibleLanding(user);
    }

    const allowed = collectAllowedPathnames(user);
    if (allowed.length === 0) {
        return resolveFirstAccessibleLanding(user);
    }

    const allowedSet = new Set(allowed);
    if (allowedSet.has(wanted)) {
        return wanted;
    }

    let bestPrefix = "";
    for (const p of allowed) {
        if (wanted.startsWith(`${p}/`) && p.length > bestPrefix.length) {
            bestPrefix = p;
        }
    }
    if (bestPrefix) {
        return bestPrefix;
    }

    let bestExtend = "";
    for (const p of allowed) {
        if (p === wanted || p.startsWith(`${wanted}/`)) {
            if (p.length > bestExtend.length) {
                bestExtend = p;
            }
        }
    }
    if (bestExtend) {
        return bestExtend;
    }

    return resolveFirstAccessibleLanding(user);
}
